import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ComponentType,
	type RefObject,
} from 'react';

// eslint-disable-next-line import/no-cycle
import { ContentOwnershipDialog } from './ContentOwnershipDialog.tsx';

type OpenModalArgs = {
	returnFocusRef?: RefObject<HTMLElement>;
};

export type ContentOwnershipContextType = {
	isModalOpen: boolean;
	openModal: (args?: OpenModalArgs) => void;
	closeModal: () => void;
	onOwnerChange?: (previousOwnerId: string, newOwnerId: string) => void;
};
const ContentOwnershipContext = createContext<ContentOwnershipContextType>({
	isModalOpen: false,
	openModal: () => {
		/* no-op */
	},
	closeModal: () => {
		/* no-op */
	},
});

type ContentOwnershipContextProviderProps = {
	children: React.ReactNode;
	onOwnerChange?: (previousOwnerId: string, newOwnerId: string) => void;
};
export const ContentOwnershipContextProvider = ({
	children,
	onOwnerChange,
}: ContentOwnershipContextProviderProps): React.JSX.Element => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [returnFocusRef, setReturnFocusRef] = useState<RefObject<HTMLElement> | undefined>();

	const openModal = useCallback((args?: OpenModalArgs) => {
		setIsModalOpen(true);
		if (args?.returnFocusRef) {
			setReturnFocusRef(args.returnFocusRef);
		}
	}, []);
	const closeModal = useCallback(() => setIsModalOpen(false), []);

	const contentOwnershipContextValue = useMemo(
		() => ({
			isModalOpen,
			openModal,
			closeModal,
			onOwnerChange,
		}),
		[isModalOpen, openModal, closeModal, onOwnerChange],
	);

	return (
		<ContentOwnershipContext.Provider value={contentOwnershipContextValue}>
			<>
				{children}
				<ContentOwnershipDialog shouldReturnFocusRef={returnFocusRef} />
			</>
		</ContentOwnershipContext.Provider>
	);
};

export const withContentOwnershipContextProvider =
	<P extends object>(WrappedComponent: ComponentType<P>) =>
	(
		props: P & { onOwnerChange?: (previousOwnerId: string, newOwnerId: string) => void },
	): React.JSX.Element => (
		<ContentOwnershipContextProvider onOwnerChange={props.onOwnerChange}>
			<WrappedComponent {...props} />
		</ContentOwnershipContextProvider>
	);

export const useContentOwnershipContext = (): ContentOwnershipContextType => {
	return useContext(ContentOwnershipContext);
};

export const ContentOwnershipContextConsumer = ({ children }: { children: any }): any => {
	const context = useContentOwnershipContext();
	return children(context);
};
